// The router's vocabulary and URL grammar: the rail modes, which mode each
// admin view belongs to and which build flag hides it, the dimensions a
// scope path can name, the tab encoding, and how the app reads its tab
// strips and its mode from the URL. It imports nothing, so the app shell,
// the tab model, the admin menu and the analytics layer, which sits
// underneath the API client, all read this one definition instead of
// keeping copies.

const lookup = (entries) => Object.freeze(Object.assign(Object.create(null), entries));

// The query params this module's grammar and the analytics layer read. The
// other params belong to the pages that own them; analytics sends every
// param as `url_<name>` whether or not it is listed here.
export const ROUTER_PARAM = Object.freeze({
    ACTIVE: "active",
    ADMIN_VIEW: "adminView",
    CANVAS: "cv",
    CHAT: "chat",
    DCOL: "dcol",
    DISCOVERY: "discovery",
    DTAB: "dtab",
    FINDING: "finding",
    FOCUS: "focus",
    GEN: "gen",
    GOVERNANCE_TAB: "governanceTab",
    HIST: "hist",
    IEVIDENCE: "ievidence",
    IFINDING: "ifinding",
    IGROUP: "igroup",
    INVESTIGATING: "investigating",
    INVESTIGATION: "investigation",
    INVESTIGATION_PANEL: "investigationPanel",
    INVESTIGATION_STATUS: "investigationStatus",
    ISUBJECT: "isubject",
    LEGACY_CANVAS: "canvas",
    LEGACY_KERNEL: "kernel",
    LENS: "lens",
    LOG: "log",
    MODE: "mode",
    ORD: "ord",
    PANEL: "panel",
    QUERY: "q",
    RANGE: "range",
    SCOPE: "scope",
    SECURITY_VIEW: "securityView",
    SETTINGS_VIEW: "settingsView",
    STEERS: "steers",
    STUDIO_ACTIVE: "studioActive",
    STUDIO_TABS: "studioTabs",
    TAB: "tab",
    TABS: "tabs",
    TRACE_QUERY: "tq",
    TURN: "turn",
    UI: "ui",
    UTAB: "utab",
    VIEW: "view",
    XCOL: "xcol",
});

// Rail order.
export const MODE_IDS = Object.freeze(["dashboard", "explore", "workspace", "security", "studio", "endpoints", "governance", "admin"]);

// A `mode` value older links still carry, and the mode it stands for.
const LEGACY_MODE_ALIASES = lookup({ settings: "admin" });

// Every admin view, whether or not a feature gate shows it, with its mode.
// The admin menu adds labels, permissions and gates on top.
export const ADMIN_VIEW_MODES = lookup({
    "security-overview": "security",
    discovery: "security",
    alerts: "security",
    kernels: "studio",
    canvases: "studio",
    workflows: "studio",
    skills: "studio",
    endpoints: "endpoints",
    "unassigned-endpoints": "endpoints",
    connect: "endpoints",
    "endpoint-config": "endpoints",
    rollouts: "endpoints",
    tokens: "endpoints",
    installers: "endpoints",
    "ai-services": "governance",
    "agent-policy": "governance",
    users: "admin",
    directory: "admin",
    escalations: "admin",
    "data-sources": "admin",
    notifications: "admin",
    account: "admin",
    report: "admin",
});

// The build flag each gated admin view needs. The three Security views share
// the Security tab's flag, so with it off they leave the menu and the router
// together and the Security rail entry hides; Skills needs its legacy
// per-browser flag.
const ADMIN_VIEW_FLAGS = lookup({
    "security-overview": "securityEnabled",
    discovery: "securityEnabled",
    alerts: "securityEnabled",
    skills: "skillsEnabled",
    "agent-policy": "agentPolicyEnabled",
});

// Every dimension a scope path can name, whether or not the tenant's data
// makes it visible. lib/dimensionDefs.js holds each one's metadata.
export const DIMENSION_IDS = Object.freeze([
    "agents", "providers", "endpoints", "identities", "models", "clusters", "projects", "sessions", "tools",
    "mcp_servers", "files", "commands", "hosts", "urls", "skills", "memories", "signals",
]);

// The canvases the product ships, which `cv` names by slug.
// lib/builtins/builtinCanvases.js defines them; a tenant's own canvases have
// slugs the tenant chose.
export const BUILTIN_CANVAS_SLUGS = Object.freeze([
    "security-posture", "ai-usage-overview", "adoption-and-concentration", "shadow-ai", "tools-and-resources", "token-spend",
]);

// ── Tabs ─────────────────────────────────────────────────────────

export const TAB_KINDS = new Set(["scope", "influence", "studio", "kernel", "canvas", "workflow", "trigger", "skills", "settings"]);

const STUDIO_TAB_KINDS = new Set(["studio", "kernel", "canvas", "workflow", "trigger", "skills"]);

// An Explore tab with no scope, the strip's overview.
const scopeOverviewTab = () => ({ kind: "scope", payload: "" });

// Mode is implied by tab kind so the rail / sidebar / page chrome all
// derive from a single source of truth.
export function modeForTab(tab) {
    if (!tab) return "explore";
    switch (tab.kind) {
        case "scope":     return "explore";
        case "influence": return "explore";
        case "studio":
        case "kernel":
        case "canvas":
        case "workflow":
        case "trigger":  return "studio";
        case "skills":   return "studio";
        case "settings": return "admin";
        default:         return "explore";
    }
}

export function encodeTab(tab) {
    if (!tab) return "scope:";
    const kind = TAB_KINDS.has(tab.kind) ? tab.kind : "scope";
    const payload = tab.payload || "";
    return `${kind}:${payload}`;
}

export function decodeTab(raw) {
    if (typeof raw !== "string") return scopeOverviewTab();
    const colon = raw.indexOf(":");
    if (colon < 0) {
        // Legacy single-payload form — treat as a scope tab.
        return { kind: "scope", payload: raw };
    }
    const kind = raw.slice(0, colon);
    const payload = raw.slice(colon + 1);
    if (!TAB_KINDS.has(kind)) {
        // Unknown prefix — preserve as scope so unknown future kinds
        // don't lose state on round-trip through an older client.
        return { kind: "scope", payload: raw };
    }
    return { kind, payload };
}

export function makeStudioTab() {
    return { kind: "studio", payload: "" };
}

export function makeKernelTab(command) {
    return { kind: "kernel", payload: String(command || "") };
}

export function makeCanvasTab(slug) {
    return { kind: "canvas", payload: String(slug || "") };
}

export function makeWorkflowTab(command) {
    return { kind: "workflow", payload: String(command || "") };
}

export function makeTriggerTab(command) {
    return { kind: "trigger", payload: String(command || "") };
}

export function makeSkillsTab() {
    return { kind: "skills", payload: "" };
}

export function makeSettingsTab(viewId) {
    return { kind: "settings", payload: String(viewId || "") };
}

// Translate the single-item URL grammar used before the typed Studio strip.
// `adminView` and `settingsView` are included because Skills used to be an
// Admin/Settings child; resolving its mode without translating the tab would
// otherwise land on Library's default overview.
export function legacyStudioTabFromSearchParams(searchParams) {
    const legacyView = searchParams.get(ROUTER_PARAM.VIEW);
    const legacyAdminView = searchParams.get(ROUTER_PARAM.ADMIN_VIEW) || searchParams.get(ROUTER_PARAM.SETTINGS_VIEW);
    const legacyKernel = searchParams.get(ROUTER_PARAM.LEGACY_KERNEL);
    const legacyCanvas = searchParams.get(ROUTER_PARAM.LEGACY_CANVAS);
    if (legacyView === "kernels" && legacyKernel) return makeKernelTab(legacyKernel);
    if (legacyView === "canvases" && legacyCanvas) return makeCanvasTab(legacyCanvas);
    if (legacyView === "skills" || legacyAdminView === "skills") return makeSkillsTab();
    return null;
}

function activeIndex(raw, length) {
    return Math.max(0, Math.min(length - 1, Number(raw || 0)));
}

// The Explore strip: scope tabs plus influence-trace tabs. Legacy encoding
// (unprefixed payloads are encoded scope paths) keeps existing deep links
// working, a missing `tabs` falls back to the legacy `scope`, and any other
// kind, left from the earlier unified strip, becomes the scope overview.
export function readExplorerStrip(searchParams) {
    const raw = searchParams.get(ROUTER_PARAM.TABS);
    let tabs;
    if (raw !== null) {
        tabs = raw.length ? raw.split(";").map(decodeTab) : [scopeOverviewTab()];
    } else {
        tabs = [{ kind: "scope", payload: searchParams.get(ROUTER_PARAM.SCOPE) || "" }];
    }
    tabs = tabs.map((t) => (t.kind === "scope" || t.kind === "influence") ? t : scopeOverviewTab());
    return { tabs, activeIdx: activeIndex(searchParams.get(ROUTER_PARAM.ACTIVE), tabs.length) };
}

// The Library strip: overview, kernel, canvas, workflow, trigger and skills
// tabs, synthesized from the older single-item params when `studioTabs` is
// absent so chat links like `?view=kernels&kernel=foo` still open the item.
export function readStudioStrip(searchParams) {
    const raw = searchParams.get(ROUTER_PARAM.STUDIO_TABS);
    let tabs;
    if (raw !== null && raw.length) {
        tabs = raw.split(";").map(decodeTab);
    } else {
        tabs = [legacyStudioTabFromSearchParams(searchParams) || makeStudioTab()];
    }
    tabs = tabs.filter((t) => STUDIO_TAB_KINDS.has(t.kind));
    if (!tabs.length) tabs = [makeStudioTab()];
    return { tabs, activeIdx: activeIndex(searchParams.get(ROUTER_PARAM.STUDIO_ACTIVE), tabs.length) };
}

// ── Mode ─────────────────────────────────────────────────────────

export function requestedAdminView(searchParams) {
    return searchParams.get(ROUTER_PARAM.ADMIN_VIEW) || searchParams.get(ROUTER_PARAM.SETTINGS_VIEW);
}

// The mode the URL asks for, before any permission fallback. `validModes`
// are the modes this build shows (shownModes), `defaultMode` the param-less
// landing, and `viewModes` maps each admin view it shows to its mode
// (shownViewModes). An explicit valid `mode` wins, with bookmarks from
// before AI Governance became a mode routed there; the legacy `settings`
// mode and the legacy `view` param fall back to their view's mode.
export function resolveMode(searchParams, { validModes, defaultMode, viewModes }) {
    const rawMode = searchParams.get(ROUTER_PARAM.MODE);
    const legacyView = searchParams.get(ROUTER_PARAM.VIEW);
    const adminView = requestedAdminView(searchParams);
    const routed = rawMode === "admin" && adminView === "ai-services" ? "governance" : rawMode;
    if (validModes.has(routed)) return routed;
    if (LEGACY_MODE_ALIASES[rawMode]) return viewModes[adminView] || LEGACY_MODE_ALIASES[rawMode];
    if (legacyView && viewModes[legacyView]) return viewModes[legacyView];
    return defaultMode;
}

// The admin view the URL asks for in `mode`, or null: the requested admin
// view when it belongs to the mode, else the legacy `view` when it does.
export function requestedViewFor(searchParams, mode, viewModes) {
    const adminView = requestedAdminView(searchParams);
    if (adminView && viewModes[adminView] === mode) return adminView;
    const legacyView = searchParams.get(ROUTER_PARAM.VIEW);
    if (legacyView && viewModes[legacyView] === mode) return legacyView;
    return null;
}

// ── Scope paths ──────────────────────────────────────────────────

// The steps of an encoded scope path, `dim:ids/dim:ids`, as the dimension
// and its still-encoded ids. lib/dimensionDefs.js decodes the ids; the
// analytics layer reads only the dimensions.
export function scopeSegments(path) {
    if (!path) return [];
    const segments = [];
    for (const segment of path.split("/")) {
        const i = segment.indexOf(":");
        if (i === -1) continue;
        segments.push({ dim: segment.slice(0, i), rest: segment.slice(i + 1) });
    }
    return segments;
}

// The admin views a build shows, each with its mode: every view whose flag,
// if it has one, is on. It has no prototype, so a view name read from the
// URL, such as `toString`, finds nothing.
export function shownViewModes({ securityEnabled, skillsEnabled, agentPolicyEnabled }) {
    const flags = { securityEnabled, skillsEnabled, agentPolicyEnabled };
    return lookup(Object.fromEntries(
        Object.entries(ADMIN_VIEW_MODES).filter(([id]) => !ADMIN_VIEW_FLAGS[id] || flags[ADMIN_VIEW_FLAGS[id]]),
    ));
}

// The modes a build shows: the Dashboard home unless its kill switch
// reverted the landing to Explore, and Security only with its tab enabled.
export function shownModes({ dashboardEnabled, securityEnabled }) {
    return new Set(MODE_IDS.filter((id) => (id !== "dashboard" || dashboardEnabled) && (id !== "security" || securityEnabled)));
}
